// The egg-SPEND celebration: the exact mirror of the purchase (design locked
// in the Celebration Lab, 2026-09-01). Exactly `cost` eggs stream OUT of the
// balance chip and dive back in BEHIND the item's buy button, whose stay-gold
// clone counts its price down to zero as they arrive. The card rumbles under
// the incoming fire; the chip wears the gold glow and pulses down per
// departed egg; a quiet toast is the instant receipt. Ending (reveal 6):
// a cyan energy wipe charges the button, the shockwave ring detonates with
// sparks as the card flips to its owned state, and the border double-flashes.
import { RM, sizeProfile, eggSprite, FEW_EGGS } from "./purchase-celebration";
import musicController from "../../audio/music-controller";
import type { SoundId } from "../../audio/sounds";

interface Particle {
  sx: number; sy: number; cx: number; cy: number; tx: number; ty: number;
  t0: number; dur: number; s: number; arrived: boolean;
}

function sparkBurst(x: number, y: number, n: number, colors: string[]) {
  for (let i = 0; i < n; i++) {
    const d = document.createElement("div");
    const sz = 4 + Math.random() * 4;
    d.style.cssText = `position:fixed; left:${x}px; top:${y}px; width:${sz}px; height:${sz}px; border-radius:50%; background:${colors[i % colors.length]}; z-index:2004; pointer-events:none; transition:transform .55s cubic-bezier(.1,.8,.3,1), opacity .55s;`;
    document.body.appendChild(d);
    const a = (i / n) * Math.PI * 2 + Math.random() * 0.5, r = 34 + Math.random() * 40;
    requestAnimationFrame(() => { d.style.transform = `translate(${Math.cos(a) * r}px,${Math.sin(a) * r}px) scale(0.4)`; d.style.opacity = "0"; });
    setTimeout(() => d.remove(), 600);
  }
}

const wait = (ms: number) => new Promise<void>((r) => setTimeout(r, RM ? Math.min(ms, 40) : ms));

export interface SpendHooks {
  /** The bought item's card slot ([data-packid]); null = skip card effects. */
  slot: HTMLElement | null;
  /** Egg cost; also the exact particle count. */
  cost: number;
  /** Item name for the receipt toast (e.g. "Finish Line"). */
  itemLabel?: string;
  /** Called once per departed egg with the running departed count. */
  onProgress: (departed: number) => void;
  /** Called at the reveal moment: flip ownership state (refreshOwned). */
  onOwn: () => void;
}

export function runEggSpend({ slot, cost, itemLabel, onProgress, onOwn }: SpendHooks): Promise<void> {
  const chip = document.querySelector<HTMLElement>("[data-eggchip]");
  const chipEgg = chip?.querySelector("canvas") ?? chip;
  if (!chipEgg || cost <= 0 || RM) { onProgress(cost); onOwn(); return Promise.resolve(); }

  return new Promise((resolve) => {
    // Let the confirm modal finish closing before measuring anything.
    requestAnimationFrame(() => requestAnimationFrame(() => start(resolve)));
  });

  function start(resolve: () => void) {
    const cr = chipEgg!.getBoundingClientRect();
    const sx = cr.left + cr.width / 2, sy = cr.top + cr.height / 2;

    const btn = slot?.querySelector<HTMLElement>("[data-buy]") ?? null;
    const br = btn?.getBoundingClientRect() ?? null;
    const tx = br ? br.left + br.width / 2 : sx, ty = br ? br.top + 4 : innerHeight - 80;
    const tjx = br ? Math.max(10, br.width / 2 - 14) : 20;

    const pc = document.createElement("canvas");
    const DPR = Math.min(2, window.devicePixelRatio || 1);
    pc.width = innerWidth * DPR; pc.height = innerHeight * DPR;
    pc.style.cssText = "position:fixed; inset:0; width:100%; height:100%; pointer-events:none; z-index:2000;";
    document.body.appendChild(pc);
    const ctx = pc.getContext("2d")!;
    const sprite = eggSprite();

    // Cards inert; siblings dimmed.
    const slots = Array.from(document.querySelectorAll<HTMLElement>("[data-cardslot]"));
    for (const s of slots) {
      s.style.pointerEvents = "none";
      if (s !== slot) { s.style.transition = "opacity .3s"; s.style.opacity = "0.55"; }
    }

    // Stay-gold clone whose number counts down as eggs return to it.
    let overlay: HTMLElement | null = null, cnt: HTMLElement | null = null;
    if (br) {
      overlay = document.createElement("div");
      overlay.innerHTML = `<span style="width:12px; height:15px; border-radius:50% 50% 50% 50% / 60% 60% 42% 42%; background:radial-gradient(circle at 35% 30%, #ffe9a8, #d9a531 65%, #8a5f14); display:inline-block;"></span><span data-cnt>${cost.toLocaleString()}</span>`;
      overlay.style.cssText = `position:fixed; left:${br.left}px; top:${br.top}px; width:${br.width}px; height:${br.height}px; z-index:2001; display:flex; align-items:center; justify-content:center; gap:7px; font-family:Orbitron,sans-serif; font-weight:800; font-size:14px; border-radius:9px; background:#ffcf4d; color:#3a2600; border:1px solid #ffcf4d; pointer-events:none; font-variant-numeric:tabular-nums;`;
      document.body.appendChild(overlay);
      cnt = overlay.querySelector<HTMLElement>("[data-cnt]");
    }

    // Chip spotlight (gold glow) - it is the source this time.
    const hb = chip!.getBoundingClientRect();
    const halo = document.createElement("div");
    halo.style.cssText = `position:fixed; left:${hb.right - 120}px; top:${hb.top - 40}px; width:150px; height:120px; background:radial-gradient(ellipse at 62% 34%, rgba(255,207,77,0.16), transparent 42%); opacity:0; transition:opacity .4s; pointer-events:none; z-index:2000;`;
    document.body.appendChild(halo);
    requestAnimationFrame(() => { halo.style.opacity = "1"; });
    chip!.style.transition = "transform .3s cubic-bezier(.2,1.3,.4,1), border-color .3s, box-shadow .3s";
    chip!.style.transform = "scale(1.14)";
    chip!.style.borderColor = "rgba(255,207,77,0.9)";
    chip!.style.boxShadow = "0 0 16px rgba(255,207,77,0.4)";
    chip!.style.zIndex = "2002"; chip!.style.position = "relative";

    // Instant receipt toast.
    const toast = document.createElement("div");
    toast.innerHTML = `<span>${itemLabel ? itemLabel + " is yours!" : "It's yours!"}</span><span style="font-family:Orbitron,sans-serif; color:#ffcf4d; font-weight:800; font-size:13px;">-${cost.toLocaleString()}</span>`;
    // Same size/position treatment as the purchase receipt: taller, and on
    // desktop lifted clear of the bottom edge.
    const toastRest = matchMedia("(min-width: 700px)").matches ? "64px" : "20px";
    toast.style.cssText = "position:fixed; left:50%; bottom:-90px; transform:translateX(-50%); display:flex; align-items:center; gap:12px; background:#0e1422; color:#eaf0ff; border:1px solid rgba(40,196,212,0.45); border-radius:14px; padding:15px 22px; box-shadow:0 10px 30px rgba(0,0,0,0.5); transition:bottom .45s cubic-bezier(.2,1.4,.4,1); white-space:nowrap; font-size:14.5px; font-weight:600; z-index:2003;";
    document.body.appendChild(toast);
    requestAnimationFrame(() => { toast.style.bottom = toastRest; });

    // Card rumbles under the incoming eggs.
    const pack = slot?.querySelector<HTMLElement>("[data-pack]") ?? null;
    const styleTag = document.createElement("style");
    styleTag.textContent = "@keyframes ar-egg-rumble { 0% { transform:translate(0.6px,-0.4px) rotate(0.15deg);} 25% { transform:translate(-0.7px,0.5px) rotate(-0.2deg);} 50% { transform:translate(0.5px,0.6px) rotate(0.1deg);} 75% { transform:translate(-0.5px,-0.6px) rotate(-0.1deg);} 100% { transform:translate(0.6px,-0.4px) rotate(0.15deg);} }";
    document.head.appendChild(styleTag);
    if (pack) pack.style.animation = "ar-egg-rumble .12s linear infinite";
    if (overlay) overlay.style.animation = "ar-egg-rumble .12s linear infinite";

    const streamMs = Math.min(2600, 500 + cost * 22 / 10);
    const t0 = performance.now();
    const parts: Particle[] = [];
    for (let i = 0; i < cost; i++) {
      const jx = sx + (Math.random() * 10 - 5), jy = sy + (Math.random() * 8 - 4);
      parts.push({
        sx: jx, sy: jy,
        tx: tx + (Math.random() * 2 - 1) * tjx, ty: ty + (Math.random() * 2 - 1) * 3,
        cx: (jx + tx) / 2 + (Math.random() * 180 - 90),
        cy: Math.min(jy, ty) - 50 - Math.random() * 110,
        t0: t0 + (cost === 1 ? 0 : (i / (cost - 1)) * streamMs) + Math.random() * 30,
        dur: 640 * (0.8 + Math.random() * 0.4),
        s: 0.30 + Math.random() * 0.4,
        arrived: false,
      });
    }

    // Departure drives the chip count-down (an egg leaves the moment it spawns).
    let departed = 0, lastPulse = 0, arrived = 0, lastNote = -1e9;
    function frame(now: number) {
      ctx.clearRect(0, 0, pc.width, pc.height);
      let alive = false;
      for (const pt of parts) {
        if (pt.arrived) continue;
        const k = (now - pt.t0) / pt.dur;
        if (k < 0) { alive = true; continue; }
        if (k >= 1) {
          pt.arrived = true; arrived++;
          if (cnt) cnt.textContent = Math.max(0, cost - arrived).toLocaleString();
          // Each landing on the button has its note, as eggs landing on your
          // count do (purchase-celebration.ts): one each for a few, else
          // climbing the scale over the stream, at most one every 45 ms.
          if (cost <= FEW_EGGS) musicController.play(`egg-land-${Math.min(arrived, FEW_EGGS) - 1}` as SoundId);
          else if (now - lastNote >= 45) {
            lastNote = now;
            musicController.play(`egg-land-${Math.min(FEW_EGGS - 1, Math.floor(((arrived - 1) / cost) * FEW_EGGS))}` as SoundId);
          }
          continue;
        }
        alive = true;
        if (k < 0.12 && departed < cost) {
          // count each egg out of the balance as it launches
          const shouldHave = Math.min(cost, parts.filter((q) => now >= q.t0).length);
          while (departed < shouldHave) { departed++; onProgress(departed); }
          if (now - lastPulse > 70) {
            lastPulse = now;
            chip!.style.transform = "scale(1.2)";
            setTimeout(() => { chip!.style.transform = "scale(1.14)"; }, 60);
          }
        }
        const u = 1 - k;
        const x = u * u * pt.sx + 2 * u * k * pt.cx + k * k * pt.tx;
        const y = u * u * pt.sy + 2 * u * k * pt.cy + k * k * pt.ty;
        const sc = pt.s * sizeProfile(k), a = k > 0.92 ? (1 - k) / 0.08 : 1;
        ctx.globalAlpha = a;
        ctx.drawImage(sprite, (x - sc * 10) * DPR, (y - sc * 12) * DPR, sc * 20 * DPR, sc * 24 * DPR);
      }
      ctx.globalAlpha = 1;
      if (alive) { requestAnimationFrame(frame); return; }
      onProgress(cost);
      void reveal();
    }

    // Reveal 6: wipe charges the clone -> ownership flips -> shockwave + border flash.
    async function reveal() {
      if (pack) pack.style.animation = "";
      if (overlay) overlay.style.animation = "";
      if (overlay) {
        overlay.style.position = "fixed"; overlay.style.overflow = "hidden";
        const w = document.createElement("div");
        w.style.cssText = "position:absolute; top:0; bottom:0; left:-100%; width:100%; background:linear-gradient(90deg, transparent, rgba(40,196,212,0.75), transparent); pointer-events:none; transition:left .34s ease-in-out;";
        overlay.appendChild(w);
        requestAnimationFrame(() => { w.style.left = "100%"; });
        await wait(340);
      }
      onOwn(); // React swaps the card to its owned state under the effects
      overlay?.remove();
      // The card is owned now: give the player their clicks back immediately
      // (the Equip button is visible), while the shockwave/toast play on.
      for (const s of slots) { s.style.pointerEvents = ""; s.style.opacity = ""; }
      const target = (slot?.querySelector<HTMLElement>("[data-buy]") ?? btn ?? pack);
      if (target) {
        const tb = target.getBoundingClientRect();
        const cx2 = tb.left + tb.width / 2, cy2 = tb.top + tb.height / 2;
        const ring = document.createElement("div");
        ring.style.cssText = `position:fixed; left:${cx2 - 16}px; top:${cy2 - 16}px; width:32px; height:32px; border-radius:50%; border:2.5px solid rgba(40,196,212,0.9); z-index:2004; pointer-events:none; transition:transform .5s ease-out, opacity .5s;`;
        document.body.appendChild(ring);
        requestAnimationFrame(() => { ring.style.transform = "scale(3.4)"; ring.style.opacity = "0"; });
        sparkBurst(cx2, cy2, 10, ["#28c4d4", "#8af0fa", "#ffcf4d"]);
        target.style.transition = "transform .3s cubic-bezier(.2,1.8,.4,1)"; target.style.transform = "scale(1.22)";
        setTimeout(() => { target.style.transform = ""; }, 300);
        setTimeout(() => ring.remove(), 550);
      }
      if (pack) {
        await wait(380);
        for (let i = 0; i < 2; i++) {
          pack.style.boxShadow = "0 0 30px rgba(40,196,212,0.75)"; await wait(130);
          pack.style.boxShadow = "0 0 18px rgba(40,196,212,0.35)"; await wait(130);
        }
        setTimeout(() => { if (pack) pack.style.boxShadow = ""; }, 1200);
      }
      finish();
    }

    function finish() {
      halo.style.opacity = "0";
      chip!.style.transform = ""; chip!.style.borderColor = ""; chip!.style.boxShadow = "";
      setTimeout(() => { toast.style.bottom = "-90px"; setTimeout(() => toast.remove(), 500); }, 2500);
      setTimeout(() => {
        pc.remove(); halo.remove(); styleTag.remove();
        chip!.style.zIndex = ""; chip!.style.position = "";
      }, 500);
      resolve2();
    }
    const resolve2 = resolve;
    requestAnimationFrame(frame);
  }
}
